/**
 * A-Team van rescue — spawn logic + per-frame update + pixel-art render.
 * Extracted from GameCanvas.jsx (iteration 27) as part of the canvas
 * refactor; behavior is identical.
 */
const WORLD_W = 1280;

/**
 * Spawn the A-Team van once per race. Chooses an entry edge so the van
 * always crosses the player's current view. No-op if already spawned.
 */
export function spawnATeamVan(st) {
  if (st.aTeamVan || st.aTeamUsed) return;
  const p = st.player;
  const fromLeft = p.x > WORLD_W / 2;
  st.aTeamVan = {
    x: fromLeft ? -40 : WORLD_W + 40,
    y: p.y,
    vx: fromLeft ? 7 : -7,
    vy: 0,
    life: 360, // ~6s at 60fps
    targetY: p.y,
    hits: new Set(),
  };
  st.aTeamUsed = true;
}

/**
 * Per-frame A-Team van update. Steers toward the player, ploughs through
 * un-stunned cops within 50 px (knocks + disables for 6s), and despawns
 * once off-screen or after ~6s of life.
 */
export function updateATeamVan(st) {
  const v = st.aTeamVan;
  if (!v) return;
  const p = st.player;
  const now = performance.now();

  v.targetY = p.y;
  v.vy += (v.targetY - v.y) * 0.005;
  v.vy = Math.max(-3, Math.min(3, v.vy));
  v.x += v.vx;
  v.y += v.vy;
  v.life -= 1;

  for (let i = 0; i < st.enemies.length; i++) {
    if (v.hits.has(i)) continue;
    const e = st.enemies[i];
    if (e.disabled > now) continue;
    const dx = e.x - v.x;
    const dy = e.y - v.y;
    const d = Math.hypot(dx, dy);
    if (d < 50) {
      v.hits.add(i);
      const knock = 18;
      e.vx += Math.sign(v.vx) * knock + (dx / (d || 1)) * 6;
      e.vy += (dy / (d || 1)) * 6;
      e.x += Math.sign(v.vx) * 14;
      e.disabled = now + 6000;
      e.hp = e.max_hp;
    }
  }

  if (v.life <= 0 || v.x < -80 || v.x > WORLD_W + 80) {
    st.aTeamVan = null;
  }
}

/**
 * Pixel-art A-Team van render. Black GMC-Vandura silhouette with red
 * stripe, chrome rims, speed lines trailing behind.
 */
export function drawATeamVan(ctx, v) {
  ctx.save();
  ctx.translate(v.x, v.y);
  // Shadow
  ctx.fillStyle = "rgba(0,0,0,0.45)";
  ctx.fillRect(-30, 14, 60, 6);
  // Flip facing based on direction of travel
  const flip = v.vx < 0 ? -1 : 1;
  ctx.scale(flip, 1);
  // Body
  ctx.fillStyle = "#1B1B1B";
  ctx.fillRect(-28, -14, 56, 28);
  ctx.strokeStyle = "#000";
  ctx.lineWidth = 2;
  ctx.strokeRect(-28, -14, 56, 28);
  // Red stripe (A-Team signature)
  ctx.fillStyle = "#E63946";
  ctx.fillRect(-28, -2, 56, 4);
  // Windshield
  ctx.fillStyle = "#3E5A7A";
  ctx.fillRect(16, -10, 12, 20);
  // Chrome details
  ctx.fillStyle = "#C0C8D4";
  ctx.fillRect(-26, -12, 4, 4);
  ctx.fillRect(-26, 8, 4, 4);
  // Wheels
  ctx.fillStyle = "#0A0A0A";
  ctx.fillRect(-22, -18, 10, 6);
  ctx.fillRect(12, -18, 10, 6);
  ctx.fillRect(-22, 12, 10, 6);
  ctx.fillRect(12, 12, 10, 6);
  // Chrome rims
  ctx.fillStyle = "#C0C8D4";
  ctx.fillRect(-20, -16, 6, 2);
  ctx.fillRect(14, -16, 6, 2);
  ctx.fillRect(-20, 14, 6, 2);
  ctx.fillRect(14, 14, 6, 2);
  ctx.restore();
  // Speed lines trailing behind
  ctx.save();
  ctx.strokeStyle = "rgba(255,255,255,0.7)";
  ctx.lineWidth = 1;
  for (let i = 0; i < 4; i++) {
    const off = (Math.floor(performance.now() / 60) + i) % 4;
    const x0 = v.x - flip * (40 + off * 8);
    const y0 = v.y - 8 + i * 4;
    ctx.beginPath();
    ctx.moveTo(x0, y0);
    ctx.lineTo(x0 - flip * 18, y0);
    ctx.stroke();
  }
  ctx.restore();
}
